// Copyright 2017 Sorama B.V.

import { BindingEngine, computedFrom, Disposable, inject } from "aurelia-framework";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { ClientStatus, HardwareType, IClientService } from "services/Client/IClientService";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { ConnectedMeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { ManagementHelperFunctions } from "services/Hardware/Management/ManagementHelperFunctions";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";

export enum HardwareStatusInfo {
    Connected,
    Connecting,
    NotConnected,
    NoDevice,
}

@inject(
    IMeasurementAcquisitionService,
    "IHardwareService<MeasurementDevice>",
    IChannelSelectionPresetService,
    BindingEngine,
    IAuthentication,
    IClientService
)
export class ConnectedHardwareService implements IAuthenticationHandler {
    private measurementAcquisitionService: IMeasurementAcquisitionService;
    private measurementDeviceService: IHardwareService<MeasurementDevice>;
    private channelSelectionPresetService: IChannelSelectionPresetService;
    private bindingEngine: BindingEngine;
    private clientService: IClientService;

    private disposables: Disposable[];

    @computedFrom("measurementDeviceService.isRefreshing")
    public get isRefreshing(): boolean {
        return this.measurementDeviceService.isRefreshing;
    }

    public hardwareStatus: HardwareStatusInfo;
    public webcamStatus: HardwareStatusInfo;
    public isFirmwareUpdateAvailable = false;

    constructor(
        measurementAcquisitionService: IMeasurementAcquisitionService,
        measurementDeviceService: IHardwareService<MeasurementDevice>,
        channelSelectionPresetService: IChannelSelectionPresetService,
        bindingEngine: BindingEngine,
        authentication: IAuthentication,
        clientService: IClientService
    ) {
        this.measurementAcquisitionService = measurementAcquisitionService;
        this.measurementDeviceService = measurementDeviceService;
        this.channelSelectionPresetService = channelSelectionPresetService;
        this.bindingEngine = bindingEngine;
        this.clientService = clientService;

        this.disposables = [
            this.bindingEngine
                .propertyObserver(this.measurementDeviceService, "isRefreshing")
                .subscribe(() => this.updateHardwareStatus()),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "isReserved")
                .subscribe(() => this.updateHardwareStatus()),
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.clientStatusChanged),
        ];

        authentication.addAuthenticationHandler(this);

        this.refresh();
    }

    public tryConnect(): void {
        let refreshPromise: Promise<void> = Promise.resolve();
        if (this.measurementDeviceService.hardware.length == 0) {
            // There is no hardware to automatically connect to, so trigger refresh
            refreshPromise = this.measurementDeviceService.refresh();
        }

        refreshPromise.then(() => {
            const localDevices = ManagementHelperFunctions.filterLocalConnectedMeasurementDevices(
                this.measurementDeviceService.hardware
            );

            if (this.measurementAcquisitionService.activeSetup) {
                // Skip when a setup is already active of auto connect it disabled
            } else if (localDevices.length > 0) {
                const device = localDevices[0];
                const connectedDeviceSetup = ConnectedMeasurementDeviceSetup.fromDevice(
                    device,
                    this.channelSelectionPresetService.getDefaultPreset(device.frontEnd)
                );

                // Otherwise select a local connected device as default
                this.measurementAcquisitionService.setActiveSetup(connectedDeviceSetup);
            } else if (this.measurementDeviceService.hardware.length > 0) {
                const device = this.measurementDeviceService.hardware[0];
                const connectedDeviceSetup = ConnectedMeasurementDeviceSetup.fromDevice(
                    device,
                    this.channelSelectionPresetService.getDefaultPreset(device.frontEnd)
                );

                // Otherwise just take the first in the network
                this.measurementAcquisitionService.setActiveSetup(connectedDeviceSetup);
            }
        });
    }

    private refresh(): Promise<void> {
        this.updateHardwareStatus();

        return this.measurementDeviceService.refresh().then(() => this.updateHardwareStatus());
    }

    private updateHardwareStatus(): void {
        if (
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.frontEnds &&
            this.measurementAcquisitionService.activeSetup.frontEnds.some(
                (frontEnd) => this.measurementDeviceService.hardware.findIndex((x) => x.id === frontEnd.id) === -1
            )
        ) {
            // One of the connected frontEnds is not discovered anymore, so disconnect.
            this.measurementAcquisitionService.setActiveSetup(null);
        }

        // Sound Camera
        if (!this.measurementDeviceService.isRefreshing) {
            // No change while refreshing
            if (this.measurementAcquisitionService.isReserved.reason) {
                this.hardwareStatus = HardwareStatusInfo.NotConnected;
            } else if (this.measurementAcquisitionService.isReserved.inProgress) {
                this.hardwareStatus = HardwareStatusInfo.Connecting;
            } else if (this.measurementAcquisitionService.isReserved.available) {
                this.hardwareStatus = HardwareStatusInfo.Connected;
            } else {
                this.hardwareStatus = HardwareStatusInfo.NoDevice;
            }
        }

        // Capture Device
        if (
            this.hardwareStatus == HardwareStatusInfo.Connected &&
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.captureDevices.length > 0
        ) {
            this.webcamStatus = HardwareStatusInfo.Connected;
        } else {
            this.webcamStatus = HardwareStatusInfo.NoDevice;
        }

        // Firmware Update
        if (this.measurementAcquisitionService.activeSetup) {
            const pendingCalls = this.measurementAcquisitionService.activeSetup.frontEnds
                .reduce((all, frontEnd) => all.concat(frontEnd.frontEndConnectors), [])
                .map((connector) => {
                    const hwType: HardwareType = connector.connectionInfo.indexOf("USB") != -1 ? "dmadaq" : "dmaio";

                    return this.clientService
                        .getAvailableFirmwareVersion(hwType)
                        .then((x) => {
                            // For now, just make an int of the string. By using a string, we're able to upgrade it to other schemes
                            const hardwareVersion = parseInt(connector.version);
                            const availableVersion = parseInt(x.Version);

                            // Also check if we can use the parse result.
                            if (!hardwareVersion && !availableVersion && connector.version == x.Version) {
                                return false;
                            } else if (hardwareVersion && availableVersion && hardwareVersion >= availableVersion) {
                                return false;
                            }

                            return true;
                        })
                        .catch(() => {
                            return false;
                        });
                });

            Promise.all(pendingCalls).then((status: boolean[]) => {
                let newStatus = false;

                for (const s of status) newStatus = newStatus || s;

                this.isFirmwareUpdateAvailable = newStatus;
            });
        } else {
            this.isFirmwareUpdateAvailable = false;
        }
    }

    public onSignIn(userId: string): Promise<void> {
        return this.refresh();
    }

    public onSignOut(): Promise<void> {
        return Promise.resolve();
    }

    private clientStatusChanged = (newStatus: ClientStatus) => {
        if (newStatus === ClientStatus.Available) {
            // Refresh devices
            this.refresh();
        }
    };
}
